import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import type { PropertyGroupResponse, SplitPropertyGroupRequest } from '@/shared/api';

export type StructureAction = 'merge' | 'split' | 'archive';

/**
 * Zusammenführen, Teilen, Archivieren — die drei Wege, die Objektstruktur zu ändern. Jeder endet
 * auf dem Objekt, das danach existiert; das archivierte führt zurück in die Objektliste.
 */
export function useObjectStructure(group: PropertyGroupResponse) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<StructureAction | null>(null);

  const groups = useQuery({
    queryKey: ['property-groups'],
    queryFn: () => api.propertyGroups.list(),
    enabled: open === 'merge',
  });

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['property-groups'] }),
      queryClient.invalidateQueries({ queryKey: ['properties'] }),
    ]);

  const onError = async (error: unknown) => {
    const apiError = await toApiError(error);
    toast.error(apiError.detail ?? apiError.title);
  };

  const merge = useMutation({
    mutationFn: (targetGroupId: string) => api.propertyGroups.merge(group.id, { targetGroupId }),
    // Wie beim Archivieren: erst weg vom Objekt, das es danach nicht mehr gibt, dann nachladen.
    onSuccess: async (target) => {
      setOpen(null);
      toast.success(`Zusammengeführt zu „${target.name}"`);
      await navigate({
        to: '/property-groups/$id/stammdaten',
        params: { id: target.id },
        replace: true,
      });
      await invalidate();
    },
    onError,
  });

  const split = useMutation({
    mutationFn: (body: SplitPropertyGroupRequest) => api.propertyGroups.split(group.id, body),
    onSuccess: async (created) => {
      await invalidate();
      setOpen(null);
      toast.success(`„${created.name}" angelegt`);
      void navigate({ to: '/property-groups/$id/stammdaten', params: { id: created.id } });
    },
    onError,
  });

  const archive = useMutation({
    mutationFn: () => api.propertyGroups.archive(group.id),
    onSuccess: async () => {
      setOpen(null);
      toast.success('Objekt archiviert');
      await navigate({ to: '/property-groups', replace: true });
      await invalidate();
    },
    onError,
  });

  // Solange die Liste lädt oder fehlt, gibt es noch keine Aussage über passende Ziele.
  return {
    open,
    setOpen,
    groups: groups.data,
    groupsFailed: groups.isError,
    merge,
    split,
    archive,
  };
}

export type ObjectStructureState = ReturnType<typeof useObjectStructure>;
